/**
 * The one-time join link: /join/:token
 *
 * A new member lands here from WhatsApp after the committee approves their
 * request. The page walks them through the rest of the pipeline:
 *
 *   1. PAY      — the approved fee, one tap.
 *   2. ONBOARD  — confirm details, set the password they will sign in with.
 *   3. WAIT     — "the committee will activate you" (they cannot sign in yet).
 *
 * The link is ONE-TIME: it dies when onboarding completes (or when the
 * committee re-issues it), so a forwarded URL can never admit someone else.
 * Public — no sign-in; in production the token is signed and verified
 * server-side (CLAUDE.md §4).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  emergencyPhoneError,
  normalizeEmergencyPhone,
  formatMoney,
  normalizeEmail,
  passwordProblem,
  TIERS,
} from '../data';
import { useClub } from '../context/ClubContext';
import { usePublicGrounds } from '../hooks/usePublicGrounds';
import { PaymentPanel } from '../components/PaymentPanel';

const INPUT =
  'w-full border border-line bg-white px-3.5 py-2.5 text-sm outline-none transition-all duration-200 focus:border-gold-500 focus:ring-4 focus:ring-gold-500/20';
const LABEL = 'mb-1.5 block font-market text-[9.5px] uppercase tracking-[0.09em] text-copy-muted';

export function JoinPage() {
  const { token } = useParams();
  const { openJoinLink, payJoin, completeJoin } = useClub();

  /*
   * This page holds no club, and cannot: the visitor has never signed in and
   * the roster is not readable by anyone who has not. So the link resolves
   * server-side and returns only what these three steps need to render —
   * enough to greet them by name and tell them what they owe.
   *
   * `reload` is what each step calls after a successful write, so the page moves
   * to the next stage from the server's answer rather than from a local guess
   * about what the write did.
   */
  const [request, setRequest] = useState(null);
  const [dead, setDead] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(
    () =>
      openJoinLink(token)
        .then((data) => setRequest(data))
        .catch((err) => setDead(err?.message ?? 'This link is not valid.'))
        .finally(() => setLoading(false)),
    [openJoinLink, token],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center font-market text-xs text-copy-muted">
        Loading…
      </div>
    );
  }

  if (dead || !request) {
    return (
      <Shell>
        <Card>
          <StageBadge tone="grey">Link not active</StageBadge>
          <h1 className="mt-3 font-market text-[26px] tracking-[-0.03em] font-extrabold leading-tight">
            This link has expired
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">
            {dead ?? 'Payment links work once, for one person.'} If you still need to pay, message
            the committee and they will send you a fresh link. Nothing has been charged.
          </p>
          {/* A link opened twice lands here; if they already onboarded, the
              way in is sign-in, not a shrug. */}
          <Link
            to="/signin"
            className="mt-4 inline-block font-market text-xs uppercase tracking-wide text-gold-800 underline underline-offset-2"
          >
            Go to sign in →
          </Link>
        </Card>
      </Shell>
    );
  }

  if (request.status === 'approved') {
    return (
      <Shell>
        <Card tone="ok">
          <StageBadge tone="ok">Membership active</StageBadge>
          <h1 className="mt-3 font-market text-[26px] tracking-[-0.03em] font-extrabold leading-tight">
            You’re in, {request.name.split(' ')[0]}
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">
            The committee has activated your membership. Sign in with your mobile number or your
            email and the password you set — see you at the grounds.
          </p>
          <Link
            to="/signin"
            className="mt-5 flex w-full items-center justify-center bg-gold-500 py-3.5 font-market text-[15.5px] font-bold tracking-[-0.015em] text-ink-800 transition-all duration-200 hover:brightness-110"
          >
            Sign in
          </Link>
        </Card>
      </Shell>
    );
  }

  if (request.status === 'paid') {
    // Paid. Either the link is still live (onboarding pending) or it is
    // spent (all done, waiting on the committee).
    return request.linkStatus === 'used' || request.onboarded ? (
      <Shell>
        <Steps at={2} />
        <Card tone="ok">
          <StageBadge tone="ok">Paid · receipt {request.receipt}</StageBadge>
          <h1 className="mt-3 font-market text-[26px] tracking-[-0.03em] font-extrabold leading-tight">
            All set — one approval to go
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">
            Your payment of <b>{formatMoney(request.amount)}</b> is in and your password is set.
            The committee has been notified and will activate your membership — usually within a
            day. You’ll sign in with your mobile number or email plus your password once that
            happens.
          </p>
          <p className="mt-3 font-market text-[10.5px] text-copy-label">
            This link has now been used and will not open again.
          </p>
        </Card>
      </Shell>
    ) : (
      <OnboardStep request={request} token={token} completeJoin={completeJoin} onDone={reload} />
    );
  }

  // status === 'link_sent' → the pay step
  return <PayStep request={request} token={token} payJoin={payJoin} onPaid={reload} />;
}

/* ================= step 1: pay ================= */

function PayStep({ request, token, payJoin, onPaid }) {
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  /** The payment screenshot, in manual (QR / bank) mode. Optional. */
  const [proof, setProof] = useState(null);
  const tier = TIERS[request.tier];
  // 'razorpay' pays instantly through the gateway; 'manual' means "pay the
  // QR / account below, attach your screenshot, then confirm".
  const manual = request.payMode !== 'razorpay';

  async function pay() {
    setPaying(true);
    setError(null);
    try {
      await payJoin(token, manual ? proof : null);
      // Take the link again: the server has moved the request to 'paid' and
      // issued the receipt, and JoinPage renders the onboarding step off that
      // rather than off a guess made here.
      await onPaid();
      window.scrollTo(0, 0);
    } catch (err) {
      setError(
        err?.message ??
          'That payment did not go through. Nothing has been charged — please try again.',
      );
      setPaying(false);
    }
  }

  return (
    <Shell wide>
      <Steps at={0} />
      {/* Welcome on the left, the payment desk on the right — the account
          details used to sit under everything (owner's direction, 2 Sep 2026). */}
      <div className="grid items-start gap-6 lg:grid-cols-[1fr_388px] lg:gap-8">
        <div className="min-w-0">
          <Card>
            <StageBadge tone="pend">Approved · payment pending</StageBadge>
            <h1 className="mt-3 font-market text-[26px] tracking-[-0.03em] font-extrabold leading-tight">
              Welcome, {request.name.split(' ')[0]}
            </h1>
            <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">
              The committee has approved your request to join {request.clubName ?? 'the club'}. Pay
              your membership fee to move on — you’ll set your sign-in password right after.
            </p>

            <div className="mt-5 border border-line bg-ground px-4 py-3.5">
              <div className="font-market text-[9.5px] uppercase tracking-[0.16em] text-copy-label">
                Joining fee
              </div>
              <div className="mt-1 font-market text-[15px] font-bold tracking-[-0.02em] text-ink-700">
                {formatMoney(request.amount)}
                <span className="font-normal text-copy-muted">
                  {' '}
                  · {tier.label} membership
                  {tier.months ? `, ${tier.months} months from today` : ''}
                </span>
              </div>
            </div>
          </Card>

          <p className="mt-5 text-center font-market text-[10px] leading-relaxed text-copy-label lg:text-left">
            This is a one-time link for {request.name}. Questions?
            <br />
            Reply to the committee’s WhatsApp message.
          </p>
        </div>

        <div className="lg:sticky lg:top-6">
          <PaymentPanel
            amount={request.amount}
            payMode={request.payMode}
            upi={request.bankUpi}
            qrUrl={request.bankQrUrl}
            holder={request.bankHolder}
            account={request.bankAccount}
            ifsc={request.bankIfsc}
            details={request.bankDetails}
            proof={proof}
            onProofChange={setProof}
            onPay={() => void pay()}
            paying={paying}
            error={error}
          />
        </div>
      </div>
    </Shell>
  );
}

/* ================= step 2: onboard ================= */

function OnboardStep({ request, token, completeJoin, onDone }) {
  const grounds = usePublicGrounds();
  const [form, setForm] = useState({
    name: request.name ?? '',
    email: request.email ?? '',
    dob: request.dob ?? '',
    gender: request.gender ?? '',
    ground: request.ground ?? '',
    blood: request.blood ?? '',
    emName: request.emName ?? '',
    emPhone: request.emPhone ?? '',
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setError('');
  };

  async function submit(e) {
    e.preventDefault();
    if (saving) return;

    if (!normalizeEmail(form.email)) {
      setError('A valid email address is required — it is how you sign in and reset a password.');
      return;
    }
    // The emergency contact is optional (owner, 2 Oct 2026) — but a number
    // that IS given must be dialable and must reach someone OTHER than the
    // applicant. See data/emergency.js (QA finding F-5).
    const emError = emergencyPhoneError(form.emPhone, request.phone);
    if (emError) {
      setError(emError);
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      await completeJoin(token, {
        password,
        ...form,
        emName: form.emName.trim() || null,
        emPhone: normalizeEmergencyPhone(form.emPhone),
      });
      // Take the link again — the server has spent it and marked the request
      // onboarded, which is what moves this page to the waiting state.
      await onDone();
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.message ?? 'Could not save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <Shell>
      <Steps at={1} />
      <Card tone="ok">
        <StageBadge tone="ok">Paid {formatMoney(request.amount)} · receipt {request.receipt}</StageBadge>
        <h1 className="mt-3 font-market text-[26px] tracking-[-0.03em] font-extrabold leading-tight">
          Set up your account
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">
          Check your details and choose the password you’ll sign in with. The committee then
          gives the final approval and you’re a member.
        </p>
      </Card>

      <form onSubmit={submit} className="mt-4 border border-line bg-white p-5">
        <Section>Your details</Section>
        <div className="mb-3.5">
          <label className={LABEL}>Full name</label>
          <input className={INPUT} value={form.name} onChange={set('name')} required />
        </div>
        <div className="mb-3.5">
          <label className={LABEL}>
            Email address<span className="text-flag-500"> *</span>
          </label>
          <input
            type="email"
            autoComplete="email"
            className={INPUT}
            value={form.email}
            onChange={set('email')}
            placeholder="you@email.com"
            required
          />
          <p className="mt-1.5 font-market text-[9.5px] leading-relaxed text-copy-label">
            A sign-in identity alongside your number, and where a password reset link is sent.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="mb-3.5">
            <label className={LABEL}>Date of birth</label>
            <input type="date" className={INPUT} value={form.dob} onChange={set('dob')} />
          </div>
          <div className="mb-3.5">
            <label className={LABEL}>Gender</label>
            <select className={INPUT} value={form.gender ?? ''} onChange={set('gender')}>
              <option value="">—</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="X">Other</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="mb-3.5">
            <label className={LABEL}>Home ground</label>
            {/* The club's own grounds, and only those. Empty until the public
                list lands, so the field never offers another club's ground. */}
            <select className={INPUT} value={form.ground} onChange={set('ground')}>
              <option value="">
                {Object.keys(grounds).length ? 'Select a ground' : 'Loading…'}
              </option>
              {Object.entries(grounds).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3.5">
            <label className={LABEL}>Blood group</label>
            <select className={INPUT} value={form.blood ?? ''} onChange={set('blood')}>
              <option value="">—</option>
              {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map((b) => (
                <option key={b}>{b}</option>
              ))}
            </select>
          </div>
        </div>

        <Section>Emergency contact — optional</Section>
        <div className="grid grid-cols-2 gap-3">
          <div className="mb-3.5">
            <label className={LABEL}>Contact name</label>
            <input className={INPUT} value={form.emName} onChange={set('emName')} />
          </div>
          <div className="mb-3.5">
            <label className={LABEL}>Contact number</label>
            <input
              type="tel"
              inputMode="tel"
              className={INPUT}
              value={form.emPhone}
              onChange={set('emPhone')}
              placeholder="Any number"
            />
          </div>
        </div>

        <Section>Your password</Section>
        <div className="grid grid-cols-2 gap-3">
          <div className="mb-2">
            <label className={LABEL}>
              Password<span className="text-flag-500"> *</span>
            </label>
            <input
              type="password"
              autoComplete="off"
              className={INPUT}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              required
            />
          </div>
          <div className="mb-2">
            <label className={LABEL}>
              Repeat it<span className="text-flag-500"> *</span>
            </label>
            <input
              type="password"
              autoComplete="off"
              className={INPUT}
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                setError('');
              }}
              required
            />
          </div>
        </div>
        <p className="font-market text-[10px] leading-relaxed text-copy-label">
          At least 6 characters with a number. You’ll sign in with your mobile number OR your
          email plus this password. Forget it and we’ll email you a link to set a new one.
        </p>

        {error && <p className="mt-3 font-market text-[11px] text-flag-600">{error}</p>}

        <button
          type="submit"
          disabled={saving}
          className="mt-5 flex w-full items-center justify-center gap-2 bg-gold-500 py-3.5 font-market text-[15.5px] font-bold tracking-[-0.015em] text-ink-800 transition-all duration-200 hover:brightness-110 active:translate-y-px disabled:opacity-70"
        >
          {saving && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          )}
          {saving ? 'Saving…' : 'Save & finish'}
        </button>
        <p className="mt-2.5 text-center font-market text-[9.5px] text-copy-label">
          Saving uses up this link — it will not open again.
        </p>
      </form>
    </Shell>
  );
}

/* ================= shared pieces ================= */

function Section({ children }) {
  return (
    <div className="mb-3 mt-5 flex items-center gap-2.5 font-market text-[10px] uppercase tracking-[0.14em] text-copy-label first:mt-0 after:h-px after:flex-1 after:bg-line after:content-['']">
      {children}
    </div>
  );
}

function StageBadge({ tone, children }) {
  const tones = {
    ok: 'bg-sea-50 text-sea-600',
    pend: 'bg-gold-50 text-gold-800',
    grey: 'bg-line-chip text-copy-muted',
  };
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-1 font-market text-[9.5px] uppercase tracking-wide ${tones[tone] ?? tones.grey}`}
    >
      {children}
    </span>
  );
}

/** Pay → Set password → Committee approval, shown above every step. */
function Steps({ at }) {
  const labels = ['Pay', 'Set password', 'Approval'];
  return (
    <div className="mb-4 flex items-center gap-1.5">
      {labels.map((label, i) => (
        <div key={label} className="flex flex-1 items-center gap-1.5">
          <span
            className={`grid h-5 w-5 flex-none place-items-center rounded-full font-market text-[9px] font-bold transition-all duration-300 ${
              i < at
                ? 'bg-gold-500 text-ink-800'
                : i === at
                  ? 'border-2 border-gold-500 bg-white text-gold-800'
                  : 'bg-line text-copy-label'
            }`}
          >
            {i < at ? '✓' : i + 1}
          </span>
          <span
            className={`font-market text-[9px] uppercase tracking-wide ${i <= at ? 'text-copy' : 'text-copy-label'}`}
          >
            {label}
          </span>
          {i < labels.length - 1 && (
            <span className={`h-px flex-1 ${i < at ? 'bg-gold-500' : 'bg-line'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function Shell({ children, wide = false }) {
  // `wide` is the two-column pay step; every other step is a single card.
  const width = wide ? 'max-w-[880px]' : 'max-w-[460px]';
  return (
    <div className="min-h-screen bg-ground">
      <header className="bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_52%,#0A0E19_100%)] px-5 py-4 text-white">
        <div className={`mx-auto ${width} font-market text-lg font-extrabold leading-none tracking-[-0.025em]`}>
          Tirupur<span className="text-gold-400">·</span>Runners
          <small className="mt-1 block font-market text-[8.5px] font-normal tracking-[0.16em] text-copy-label">
            MEMBERSHIP
          </small>
        </div>
      </header>
      <main className={`mx-auto ${width} px-4 pb-14 pt-6`}>{children}</main>
    </div>
  );
}

function Card({ tone, children }) {
  return (
    <div
      className={`border bg-white p-5 ${
        tone === 'ok' ? 'border-sea-300' : 'border-line'
      }`}
    >
      {children}
    </div>
  );
}
